import { Initiative, Feature, WorkProduct } from '../types/index';

// Platform terminology constants
export const PLATFORM_TERMINOLOGY = {
  entities: {
    project: 'Project',
    feature: 'Feature', 
    workProduct: 'Work Product',
    initiative: 'Initiative', // Legacy term, being phased out
    phase: 'SDLC Phase',
    requirement: 'Detailed Requirement'
  },
  actions: {
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    promote: 'Promote',
    activate: 'Activate',
    generate: 'Generate',
    approve: 'Approve'
  },
  statuses: {
    active: 'Active',
    planning: 'Planning',
    completed: 'Completed',
    archived: 'Archived',
    generating: 'Generating',
    available: 'Available',
    disabled: 'Disabled'
  }
} as const;

// Reserved words that cannot be used in entity names
export const RESERVED_WORDS = [
  'admin', 'api', 'app', 'auth', 'config', 'dashboard', 'default', 'delete', 
  'edit', 'error', 'export', 'import', 'index', 'login', 'logout', 'new', 
  'null', 'root', 'settings', 'system', 'temp', 'test', 'undefined', 'user'
];

// Validation patterns
export const VALIDATION_PATTERNS = {
  entityName: /^[a-zA-Z0-9\s\-_()]{3,100}$/,
  description: /^.{10,500}$/,
  label: /^[a-zA-Z0-9\-_]{2,30}$/,
  code: /^[A-Z]{2,6}-[0-9]{1,4}$/
};

// Validation error messages
export const VALIDATION_MESSAGES = {
  entityName: {
    required: 'Name is required',
    minLength: 'Name must be at least 3 characters long',
    maxLength: 'Name cannot exceed 100 characters',
    pattern: 'Name can only contain letters, numbers, spaces, hyphens, underscores, and parentheses',
    reserved: 'This name is reserved and cannot be used',
    duplicate: 'A {entityType} with this name already exists'
  },
  description: {
    required: 'Description is required',
    minLength: 'Description must be at least 10 characters long',
    maxLength: 'Description cannot exceed 500 characters'
  },
  label: {
    pattern: 'Labels can only contain letters, numbers, hyphens, and underscores',
    maxLength: 'Labels cannot exceed 30 characters',
    duplicate: 'This label already exists'
  },
  code: {
    pattern: 'Code must follow format: XX-123 (2-6 uppercase letters, hyphen, 1-4 digits)',
    duplicate: 'This code is already in use'
  }
};

// Entity validation functions
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

// Extended validation result for terminology validation
export interface TerminologyValidationResult extends ValidationResult {
  suggestions: string[];
}

export function validateEntityName(
  name: string,
  entityType: keyof typeof PLATFORM_TERMINOLOGY.entities,
  existingEntities: Array<{ name: string }> = []
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Required check
  if (!name || name.trim().length === 0) {
    errors.push(VALIDATION_MESSAGES.entityName.required);
    return { isValid: false, errors, warnings };
  }

  const trimmedName = name.trim();

  // Length validation
  if (trimmedName.length < 3) {
    errors.push(VALIDATION_MESSAGES.entityName.minLength);
  }
  if (trimmedName.length > 100) {
    errors.push(VALIDATION_MESSAGES.entityName.maxLength);
  }

  // Pattern validation
  if (!VALIDATION_PATTERNS.entityName.test(trimmedName)) {
    errors.push(VALIDATION_MESSAGES.entityName.pattern);
  }

  // Reserved words check
  if (RESERVED_WORDS.includes(trimmedName.toLowerCase())) {
    errors.push(VALIDATION_MESSAGES.entityName.reserved);
  }

  // Duplicate check
  const isDuplicate = existingEntities.some(
    entity => entity.name.toLowerCase() === trimmedName.toLowerCase()
  );
  if (isDuplicate) {
    errors.push(
      VALIDATION_MESSAGES.entityName.duplicate.replace(
        '{entityType}',
        PLATFORM_TERMINOLOGY.entities[entityType]
      )
    );
  }

  // Terminology warnings
  if (entityType === 'project' && trimmedName.toLowerCase().includes('initiative')) {
    warnings.push(
      'Consider using "Project" terminology instead of "Initiative" for consistency'
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

export function validateDescription(description: string): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!description || description.trim().length === 0) {
    errors.push(VALIDATION_MESSAGES.description.required);
    return { isValid: false, errors, warnings };
  }

  const trimmedDescription = description.trim();

  if (trimmedDescription.length < 10) {
    errors.push(VALIDATION_MESSAGES.description.minLength);
  }
  if (trimmedDescription.length > 500) {
    errors.push(VALIDATION_MESSAGES.description.maxLength);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

export function validateLabels(
  labels: string[],
  existingLabels: string[] = []
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  for (const label of labels) {
    if (!VALIDATION_PATTERNS.label.test(label)) {
      errors.push(`"${label}": ${VALIDATION_MESSAGES.label.pattern}`);
    }
    if (label.length > 30) {
      errors.push(`"${label}": ${VALIDATION_MESSAGES.label.maxLength}`);
    }
  }

  // Check for duplicates within the array
  const duplicates = labels.filter((label, index) => 
    labels.indexOf(label) !== index
  );
  if (duplicates.length > 0) {
    errors.push(`Duplicate labels found: ${duplicates.join(', ')}`);
  }

  // Check against existing labels
  const conflictingLabels = labels.filter(label =>
    existingLabels.includes(label)
  );
  if (conflictingLabels.length > 0) {
    warnings.push(`These labels already exist: ${conflictingLabels.join(', ')}`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

export function validateProjectCode(
  code: string,
  existingCodes: string[] = []
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (code && !VALIDATION_PATTERNS.code.test(code)) {
    errors.push(VALIDATION_MESSAGES.code.pattern);
  }

  if (code && existingCodes.includes(code.toUpperCase())) {
    errors.push(VALIDATION_MESSAGES.code.duplicate);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

// Comprehensive entity validation
export function validateProject(
  projectData: Partial<Initiative>,
  existingProjects: Initiative[] = []
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Validate name
  if (projectData.name) {
    const nameValidation = validateEntityName(
      projectData.name,
      'project',
      existingProjects
    );
    errors.push(...nameValidation.errors);
    warnings.push(...nameValidation.warnings);
  }

  // Validate description
  if (projectData.description) {
    const descValidation = validateDescription(projectData.description);
    errors.push(...descValidation.errors);
    warnings.push(...descValidation.warnings);
  }

  // Validate code (if project has code property)
  if ('code' in projectData && projectData.code) {
    const codeValidation = validateProjectCode(
      projectData.code as string,
      existingProjects
        .map(p => ('code' in p ? (p as any).code : null))
        .filter(Boolean) as string[]
    );
    errors.push(...codeValidation.errors);
    warnings.push(...codeValidation.warnings);
  }

  // Validate labels
  if (projectData.labels && projectData.labels.length > 0) {
    const allExistingLabels = existingProjects
      .flatMap(p => p.labels || [])
      .filter((label, index, array) => array.indexOf(label) === index);

    const labelsValidation = validateLabels([...projectData.labels], [...allExistingLabels]);
    errors.push(...labelsValidation.errors);
    warnings.push(...labelsValidation.warnings);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

export function validateFeature(
  featureData: Partial<Feature>,
  existingFeatures: Feature[] = []
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Validate name
  if (featureData.name) {
    const nameValidation = validateEntityName(
      featureData.name,
      'feature',
      existingFeatures
    );
    errors.push(...nameValidation.errors);
    warnings.push(...nameValidation.warnings);
  }

  // Validate labels
  if (featureData.labels && featureData.labels.length > 0) {
    const allExistingLabels = existingFeatures
      .flatMap(f => f.labels || [])
      .filter((label, index, array) => array.indexOf(label) === index);

    const labelsValidation = validateLabels([...featureData.labels], [...allExistingLabels]);
    errors.push(...labelsValidation.errors);
    warnings.push(...labelsValidation.warnings);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

// Terminology guidance content
export const TERMINOLOGY_GUIDANCE = {
  project: {
    title: 'Product Naming Guidelines',
    description: 'Products represent high-level initiatives or software development efforts.',
    guidelines: [
      'Use descriptive, business-focused names (e.g., "Smart Inventory Sync")',
      'Avoid technical jargon unless necessary',
      'Keep names concise but meaningful (3-100 characters)',
      'Use proper capitalization and spacing',
      'Consider including the target user or business area'
    ],
    examples: {
      good: ['Smart Inventory Sync', 'Mobile Banking App v2.0', 'Inventory Management System'],
      bad: ['proj123', 'test', 'NewThing', 'URGENT_FIX']
    }
  },
  feature: {
    title: 'Feature Naming Guidelines',
    description: 'Features represent specific functionality within a product.',
    guidelines: [
      'Focus on user-facing functionality (e.g., "User Authentication")',
      'Use action-oriented names when appropriate',
      'Be specific about the capability or user need',
      'Maintain consistency with product terminology',
      'Consider the user\'s perspective'
    ],
    examples: {
      good: ['User Authentication', 'Payment Processing', 'Real-time Notifications', 'Data Export'],
      bad: ['auth', 'payments', 'thing1', 'module_x']
    }
  },
  workProduct: {
    title: 'Work Product Guidelines',
    description: 'Work Products are standardized deliverables in the SDLC process.',
    guidelines: [
      'Work Product names are predefined and cannot be changed',
      'Focus on customizing content rather than names',
      'Follow the established SDLC methodology',
      'Ensure proper sequencing and dependencies',
      'Maintain traceability between related work products'
    ]
  },
  labels: {
    title: 'Label Guidelines',
    description: 'Labels help organize and categorize products and features.',
    guidelines: [
      'Use consistent, standardized labels across the platform',
      'Keep labels short and descriptive (2-30 characters)',
      'Use kebab-case or camelCase for multi-word labels',
      'Consider creating a label taxonomy for your organization',
      'Avoid special characters except hyphens and underscores'
    ],
    examples: {
      good: ['high-priority', 'customer-facing', 'mobile', 'security', 'v2-migration'],
      bad: ['!!!URGENT!!!', 'misc stuff', 'label 1', 'temp@label']
    }
  }
};

// Auto-suggestion functions
export function suggestProjectName(description: string): string[] {
  const suggestions: string[] = [];
  const words = description.toLowerCase().split(/\s+/);
  
  // Extract key business terms
  const businessTerms = words.filter(word => 
    !['the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from', 'about'].includes(word)
  );

  if (businessTerms.length >= 2) {
    // Capitalize first letter of each word
    const formatted = businessTerms
      .slice(0, 4) // Take up to 4 key words
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    suggestions.push(formatted);
    suggestions.push(`${formatted} Product`);
    suggestions.push(`${formatted} Initiative`);
  }

  return suggestions.slice(0, 3); // Return top 3 suggestions
}

export function suggestLabels(name: string, description: string): string[] {
  const suggestions: string[] = [];
  const text = `${name} ${description}`.toLowerCase();

  // Common label patterns
  const labelPatterns = [
    { pattern: /mobile|phone|app/i, label: 'mobile' },
    { pattern: /web|website|portal/i, label: 'web' },
    { pattern: /api|service|backend/i, label: 'backend' },
    { pattern: /ui|ux|frontend|interface/i, label: 'frontend' },
    { pattern: /security|auth|login/i, label: 'security' },
    { pattern: /data|database|analytics/i, label: 'data' },
    { pattern: /customer|user|client/i, label: 'customer-facing' },
    { pattern: /admin|management|internal/i, label: 'internal' },
    { pattern: /urgent|critical|priority/i, label: 'high-priority' },
    { pattern: /integration|sync|connect/i, label: 'integration' }
  ];

  labelPatterns.forEach(({ pattern, label }) => {
    if (pattern.test(text) && !suggestions.includes(label)) {
      suggestions.push(label);
    }
  });

  return suggestions.slice(0, 5); // Return top 5 suggestions
}

// Tag/terminology validation function for UploadCenter
export function validateTerminology(term: string): TerminologyValidationResult {
  const normalizedTerm = term.toLowerCase().trim();
  const suggestions: string[] = [];
  
  // Common terminology patterns and their suggested replacements
  const terminologyMap: Record<string, string[]> = {
    'work-thing': ['Work Product', 'Work Item', 'Deliverable'],
    'workproduct': ['Work Product'],
    'work-product': ['Work Product'],
    'work product': ['Work Product'],
    'task': ['Work Product', 'Activity'],
    'deliverable': ['Work Product', 'Output'],
    'artifact': ['Work Product', 'Deliverable'],
    'output': ['Work Product', 'Deliverable'],
    'item': ['Work Product', 'Component'],
    'thing': ['Work Product', 'Component', 'Element'],
    'stuff': ['Work Product', 'Component', 'Item'],
    'project': ['Product', 'Initiative'],
    'proj': ['Product'],
    'initiative': ['Product', 'Initiative'],
    'feature': ['Feature', 'Capability'],
    'feat': ['Feature'],
    'requirement': ['Detailed Requirement', 'Requirement'],
    'req': ['Requirement', 'Detailed Requirement'],
    'spec': ['Specification', 'Detailed Requirement'],
    'document': ['Work Product', 'Documentation'],
    'doc': ['Document', 'Work Product'],
    'analysis': ['Analysis', 'Research'],
    'research': ['Research', 'Analysis'],
    'design': ['Design', 'Architecture'],
    'mockup': ['UI Mockup', 'Design'],
    'wireframe': ['Wireframe', 'UI Mockup'],
    'prototype': ['Prototype', 'UI Prototype'],
    'flow': ['User Flow', 'Process Flow'],
    'journey': ['User Journey', 'User Flow'],
    'persona': ['User Persona', 'Persona'],
    'user': ['User', 'User Persona'],
    'story': ['User Story', 'Requirement'],
    'epic': ['Epic', 'Feature'],
    'sprint': ['Sprint', 'Iteration'],
    'iteration': ['Iteration', 'Sprint'],
    'phase': ['SDLC Phase', 'Phase'],
    'stage': ['Stage', 'SDLC Phase'],
    'milestone': ['Milestone', 'Deliverable'],
    'goal': ['Goal', 'Objective'],
    'objective': ['Objective', 'Goal'],
    'metric': ['Success Metric', 'KPI'],
    'kpi': ['KPI', 'Success Metric'],
    'test': ['Test Case', 'Testing'],
    'testing': ['Testing', 'QA'],
    'qa': ['QA', 'Quality Assurance'],
    'deployment': ['Deployment', 'Release'],
    'release': ['Release', 'Deployment'],
    'launch': ['Launch', 'Release'],
    'rollout': ['Rollout', 'Deployment']
  };

  // Check for exact matches or partial matches
  let isValid = true;
  
  // Check if the term is a known good term
  const standardTerms = [
    'work product', 'project', 'feature', 'initiative', 'requirement', 
    'analysis', 'design', 'development', 'testing', 'deployment',
    'user story', 'epic', 'sprint', 'milestone', 'goal', 'metric'
  ];
  
  if (!standardTerms.some(standard => 
    normalizedTerm.includes(standard) || standard.includes(normalizedTerm)
  )) {
    // Look for similar terms
    Object.keys(terminologyMap).forEach(key => {
      if (normalizedTerm.includes(key) || key.includes(normalizedTerm)) {
        suggestions.push(...terminologyMap[key]);
        isValid = false;
      }
    });
    
    // If no similar terms found but the term seems non-standard
    if (suggestions.length === 0 && normalizedTerm.length > 0) {
      // Suggest generic work product terms for unknown terms
      if (!isValid || normalizedTerm.length < 3) {
        suggestions.push('Work Product', 'Component', 'Deliverable');
        isValid = false;
      }
    }
  }
  
  // Remove duplicates and limit suggestions
  const uniqueSuggestions = [...new Set(suggestions)].slice(0, 3);
  
  return {
    isValid: isValid && uniqueSuggestions.length === 0,
    errors: isValid ? [] : [`"${term}" is not a recognized platform term`],
    warnings: [],
    suggestions: uniqueSuggestions
  };
}